import React from "react";
import { Sparkles } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";

/**
 * FeaturedAtTrulieveRow — horizontal-scroll strip that highlights 8
 * products from Trulieve's PA-featured brands (Modern Flower, Cultivar,
 * Roll One / R.O., Momenta, Khalifa Kush). Rendered above the main
 * dispensary menu so the shelf leads with signature brands.
 *
 * On-sale detection is intentionally client-side + naive: we check
 * whether any of the shop's active `sales` banners mention the
 * product's brand string. Trulieve's product API doesn't ship a per-
 * SKU `original_price`, so exact discount math would need banner-rule
 * parsing per product — the "SALE" ribbon here is a UX hint, and
 * tapping the card sends the shopper into `/sales/{id}` where the
 * server-parsed rules take over the qualifying logic.
 *
 * Round-robin category picking mixes flower / vape / edible / etc. so
 * the row doesn't cluster into 8 flower jars in a row.
 */

// Trulieve PA featured brand-key set — mirrors the backend
// `_FEATURED_TRULIEVE_BRANDS_PA` in `server.py`. Match is done via
// _brandKey below so ingest variants ("R.O.", "R.O", "ro") collapse.
const FEATURED_KEYS = new Set([
  "modernflower",
  "cultivar",
  "ro",
  "rollone",
  "momenta",
  "khalifakush",
]);
const _brandKey = (b) => (b || "").toLowerCase().replace(/[^a-z0-9]+/g, "");

// Human-readable brand order for stable presentation.
const BRAND_ORDER = [
  "Modern Flower",
  "R.O.",
  "Roll One",
  "Momenta",
  "Cultivar",
  "Khalifa Kush",
];

const roundRobinPick = (products, take) => {
  // Bucket by category so we can interleave — a "featured" strip that
  // shows 8 flower jars in a row looks like a category grid, not a
  // signature-brand highlight.
  const buckets = new Map();
  for (const p of products) {
    const c = (p.category || "flower").toLowerCase();
    if (!buckets.has(c)) buckets.set(c, []);
    buckets.get(c).push(p);
  }
  // Order category buckets by BRAND_ORDER prevalence — this keeps a
  // stable "featured brand first" ordering across page loads instead of
  // depending on Map insertion, which can flip when the underlying
  // menu re-fetches.
  const catList = Array.from(buckets.keys()).sort();
  const out = [];
  let i = 0;
  const guard = products.length + 10; // paranoia — never loop forever
  let steps = 0;
  while (out.length < take && steps < guard) {
    const cat = catList[i % catList.length];
    const bucket = buckets.get(cat);
    if (bucket && bucket.length) {
      out.push(bucket.shift());
    }
    i += 1;
    steps += 1;
    if (catList.every((c) => (buckets.get(c) || []).length === 0)) break;
  }
  return out;
};

export function FeaturedAtTrulieveRow({ menu = [], sales = [], take = 8 }) {
  // 1) Featured-brand + in-stock only.
  const pool = menu.filter(
    (p) =>
      FEATURED_KEYS.has(_brandKey(p.brand)) &&
      p.stock_status === "in_stock" &&
      p.image_url,
  );
  if (pool.length === 0) return null;

  // 2) Prefer brands mentioned by an active sale banner headline — the
  // shopper's mental model is "if there's a sale on X, I want to see X
  // first". Rank: brand-mentioned-in-sale > everything-else, then
  // brand-order tiebreak, then price ascending.
  const saleBrandKeys = new Set();
  for (const s of sales) {
    const text = `${s.headline || ""} ${s.description || s.title || ""}`
      .toLowerCase();
    for (const key of FEATURED_KEYS) {
      // Rebuild a compact readable version to substring-match on the
      // banner text. e.g. "modernflower" → "modern flower".
      const readable = key
        .replace("modernflower", "modern flower")
        .replace("rollone", "roll one")
        .replace("khalifakush", "khalifa kush");
      if (text.includes(readable) || (key === "ro" && /\br\.?o\.?\b/.test(text))) {
        saleBrandKeys.add(key);
      }
    }
  }

  const brandOrderRank = (p) => {
    const idx = BRAND_ORDER.indexOf(p.brand);
    return idx === -1 ? 99 : idx;
  };

  const ranked = [...pool].sort((a, b) => {
    const aOnSale = saleBrandKeys.has(_brandKey(a.brand)) ? 0 : 1;
    const bOnSale = saleBrandKeys.has(_brandKey(b.brand)) ? 0 : 1;
    if (aOnSale !== bOnSale) return aOnSale - bOnSale;
    const aOrder = brandOrderRank(a);
    const bOrder = brandOrderRank(b);
    if (aOrder !== bOrder) return aOrder - bOrder;
    return (a.price || 0) - (b.price || 0);
  });

  const picks = roundRobinPick(ranked, take);
  if (picks.length === 0) return null;

  return (
    <section
      className="mb-8 -mx-4 sm:mx-0"
      data-testid="featured-at-trulieve-row"
    >
      <div className="px-4 sm:px-0 flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-[#d4a268]" strokeWidth={2} />
        <h3 className="font-serif text-xl leading-none">
          Featured at Trulieve
        </h3>
        <span className="text-[10px] uppercase tracking-[0.18em] text-foreground/45 ml-2">
          {saleBrandKeys.size > 0 ? "On sale now" : "Signature brands"}
        </span>
      </div>
      <div
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar px-4 sm:px-0 pb-2"
        data-testid="featured-at-trulieve-scroller"
      >
        {picks.map((p) => (
          <div
            key={p.id}
            className="snap-start shrink-0 w-[240px] sm:w-[260px] relative"
            data-testid={`featured-pick-${p.id}`}
          >
            {saleBrandKeys.has(_brandKey(p.brand)) && (
              <span
                className="absolute top-3 left-3 z-10 inline-flex items-center rounded-full bg-[#d4a268] text-[10px] font-semibold uppercase tracking-[0.18em] text-[#1a1006] px-2.5 h-6"
                data-testid={`featured-pick-sale-ribbon-${p.id}`}
              >
                Sale
              </span>
            )}
            <ProductCard
              product={p}
              utm={{
                medium: "featured",
                campaign: "trulieve-featured-row",
                content: `brand-${_brandKey(p.brand)}`,
              }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
